<template>
  <div class="impact-page">
    <impact-header title="学生异动影响" :tags="['被波及 → 换老师 → 保时 / 改时 → 留存']"
                   :chips="chips" :loaded-at="loadedAt"></impact-header>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
      </div>
      <div class="section-filters">
        <filter-pills v-model="days" :options="dayOptions" label="时间窗口" @on-change="load"></filter-pills>
        <filter-pills v-model="type" :options="typeOptions" label="异动类型" @on-change="load"></filter-pills>
        <filter-pills v-model="newOnly" :options="newOptions" @on-change="load"></filter-pills>
        <span class="filter-note">※ 窗口：生效日在近 {{ days }} 天（含今天）· 学生按最早一起事件归因</span>
      </div>
      <div class="kpi-grid">
        <Spin v-if="loading" fix></Spin>
        <kpi-card v-for="card in kpiCards" :key="card.key" v-bind="card"></kpi-card>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">学生走到哪一步</h2>
      </div>
      <p class="block-lead">每个学生都在这条链上：被波及 → 换老师 → 保住或改掉原时段 → 留存或流失。看学生卡在哪一步。</p>
      <div class="grid g21">
        <div class="card">
          <div class="card-head">
            <span class="card-title">学生状态链路<span class="card-sub">近 {{ days }} 天 · {{ fmtNum(d.studentCount) }} 名学生</span></span>
          </div>
          <div v-for="row in chainRows" :key="row.key" class="funnel-row">
            <div class="funnel-label">{{ row.label }}</div>
            <div class="funnel-track">
              <div class="funnel-fill" :style="{ width: row.width + '%', background: row.color }">{{ fmtNum(row.value) }}</div>
            </div>
            <div class="funnel-rate">{{ row.rate }}</div>
          </div>
          <div class="note is-warning">
            盯两个缺口：<b>「待换老师」是运营的待办清单</b>（当前 {{ fmtNum(d.waitingCount) }} 人，超 7 天 {{ fmtNum(d.waitingOver7Count) }} 人），「改时间」是下一阶段流失的种子（{{ fmtNum(d.changeSlotCount) }} 人）。
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">损失课次分布<span class="card-sub">按学生 · 生效后 14 天应上未上</span></span>
          </div>
          <bar-list :rows="depthRows" unit="人"></bar-list>
          <div class="note is-danger" v-if="depthInsight">{{ depthInsight }}</div>
        </div>
      </div>

      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">换老师的两种结果<span class="card-sub">{{ fmtNum(d.switchedCount) }} 名已换完学生 · 按是否保住原时段拆分</span></span>
          </div>
          <div class="compare-boxes">
            <div class="compare-box">
              <div class="compare-box-title is-healthy">保时换老师</div>
              <div class="compare-box-value">{{ fmtNum(d.keepSlotCount) }}<small>人</small></div>
              <div class="compare-box-foot">原时段不变 · 占已换 {{ fmtRate(d.keepSlotRate) }} · 30 天留存 <b>{{ fmtRate(d.keepRetain30Rate) }}</b></div>
            </div>
            <div class="compare-box is-danger">
              <div class="compare-box-title is-danger">换老师 + 被迫改时间</div>
              <div class="compare-box-value is-danger">{{ fmtNum(d.changeSlotCount) }}<small>人</small></div>
              <div class="compare-box-foot">时段也变了 · 占已换 {{ fmtRate(d.changeSlotRate) }} · 30 天留存 <b>{{ fmtRate(d.changeRetain30Rate) }}</b></div>
            </div>
          </div>
          <div class="note is-danger" v-if="retainGap != null">
            同样是换老师，保时和改时的 30 天留存相差 <b>{{ retainGap }}pp</b>。真正伤人的往往不是换老师本身，而是「换人 + 作息也变了」。
          </div>
          <div class="note is-warning">
            排课侧建议：<b>先锁时段，再配老师</b>。宁可多等两天找一个能上原时段的老师，也别急着塞一个时段错位的。
          </div>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">各类型保时率<span class="card-sub">换老师后仍在原时段（±30 分钟）的比例</span></span>
          </div>
          <bar-list :rows="keepRows" unit="" :right-width="130"></bar-list>
          <dl class="kv">
            <dt>保时换老师 30 天留存</dt><dd class="t-healthy">{{ fmtRate(d.keepRetain30Rate) }}</dd>
            <dt>改时换老师 30 天留存</dt><dd class="t-danger">{{ fmtRate(d.changeRetain30Rate) }}</dd>
            <dt>留存差值</dt><dd>{{ retainGap == null ? '—' : retainGap + 'pp' }}</dd>
          </dl>
          <div class="note">关闭时间关掉的正是学生原来上课的时段，要保时只能找同档期的其他老师，保时率天然更难。</div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">受影响学生清单</h2>
        <span class="section-right">{{ listSub }}</span>
      </div>
      <div class="grid">
        <div class="card">
          <div class="card-head">
            <span class="card-title">学生清单<span class="card-sub">前 50 名 · 导出为全部</span></span>
            <span class="card-link" :class="{ 'is-disabled': exporting }" @click="doExport">{{ exporting ? '导出中…' : '导出名单 →' }}</span>
          </div>
          <div class="section-filters list-filters">
            <span class="filter-group teacher-filter">
              <span class="filter-label">原老师</span>
              <Select v-model="tchId" filterable clearable placeholder="全部" style="width: 200px" @on-change="onTchChange">
                <Option v-for="t in teachers" :key="t.id" :value="t.id">{{ t.name || t.id }}</Option>
              </Select>
            </span>
            <filter-pills v-model="switchStatus" :options="switchOptions" label="换老师状态" @on-change="load"></filter-pills>
            <span class="filter-note">※ 原老师 / 换老师状态只过滤下方名单与导出，上方指标不变</span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr>
                <th>学生</th>
                <th>班主任</th>
                <th>新生状态</th>
                <th>原老师</th>
                <th>异动类型</th>
                <th>生效日</th>
                <th>原时段</th>
                <th>换老师状态</th>
                <th>新老师</th>
                <th class="num">迁移天数</th>
                <th class="num">损失课次</th>
                <th class="num">剩余课时</th>
                <th>风险</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in list" :key="row.uid + '-' + row.tchId + '-' + row.effectiveDate"
                  :class="{ 'is-priority': row.newPriority }">
                <td><span class="t-name">{{ row.userName || '—' }}</span><span class="t-id">{{ row.uid }}</span></td>
                <td>{{ row.adviserName || '—' }}</td>
                <td>
                  <span v-if="row.newPriority" class="badge is-danger">新生优先</span>
                  <span class="t-muted">{{ isNum(row.tenureDays) ? '开课第 ' + row.tenureDays + ' 天' : '—' }}</span>
                </td>
                <td>{{ row.tchName || row.tchId }}</td>
                <td><span class="badge" :class="typeBadge(row.type)">{{ typeLabel(row.type) }}</span></td>
                <td>{{ row.effectiveDate || '—' }}</td>
                <td>{{ row.origSlot || '—' }}</td>
                <td>
                  <span v-if="row.switchStatus === 'waiting'" class="badge is-warning">待换老师</span>
                  <template v-else>
                    <span class="badge is-healthy">已换完</span>
                    <span v-if="row.keepSlot === true" class="badge is-healthy">保时</span>
                    <span v-else-if="row.keepSlot === false" class="badge is-danger">改时间</span>
                  </template>
                </td>
                <td>{{ row.newTchName || '—' }}</td>
                <td class="num">{{ isNum(row.switchDays) ? row.switchDays + ' 天' : '—' }}</td>
                <td class="num"><b :class="{ 't-danger': num(row.lostLessons) >= 4 }">{{ fmtNum(row.lostLessons) }}</b></td>
                <td class="num">{{ fmtNum(row.remainHours) }}</td>
                <td><span class="badge" :class="levelBadge(row.level)">{{ row.level || '—' }}</span><span class="t-muted"> {{ fmtNum(row.score) }}</span></td>
              </tr>
              <tr v-if="!list.length"><td colspan="13" class="empty">当前筛选下没有受影响学生</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">换老师时长分布<span class="card-sub">已换完学生 · 生效日到首次已选新老师课</span></span>
          </div>
          <bar-list :rows="regapRows" unit="人"></bar-list>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">异动类型 × 学生结果<span class="card-sub">学生经历了什么，而不是老师发生了什么</span></span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr>
                <th>异动类型</th>
                <th class="num">波及学生</th>
                <th class="num">已换完</th>
                <th class="num">7 天换完率</th>
                <th class="num">保时率</th>
                <th class="num">人均损失课次</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in byType" :key="row.type">
                <td><span class="badge" :class="typeBadge(row.type)">{{ typeLabel(row.type) }}</span></td>
                <td class="num">{{ fmtNum(row.students) }}</td>
                <td class="num">{{ fmtNum(row.switched) }}</td>
                <td class="num">{{ fmtRate(row.switched7dRate) }}</td>
                <td class="num" :class="{ 't-danger': isNum(row.keepSlotRate) && row.keepSlotRate < 70 }">{{ fmtRate(row.keepSlotRate) }}</td>
                <td class="num">{{ fmtNum(row.avgLost) }}</td>
              </tr>
              <tr v-if="!byType.length"><td colspan="6" class="empty">暂无数据</td></tr>
              </tbody>
            </table>
          </div>
          <div class="note is-danger">人均损失课次高的类型，说明学生在换老师之前就已经断课了。这类学生换完老师要加一轮主动沟通和首课质检。</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ImpactHeader from './parts/ImpactHeader.vue'
import KpiCard from './parts/KpiCard.vue'
import BarList from './parts/BarList.vue'
import FilterPills from './parts/FilterPills.vue'
import {
  postImpact, exportImpact, num, isNum, fmtNum, fmtRate, fmtRateNum, typeLabel, typeColor, typeBadge,
  levelHigh, levelLow, DAY_OPTIONS, LEVEL_BADGE
} from './parts/impact'

const DEPTH_COLORS = ['#0B9E8D', '#8FA3C7', '#E89F2D', '#E6464E', '#B3312F']
const REGAP_COLORS = ['#0B9E8D', '#5FA9A0', '#8FA3C7', '#E89F2D', '#E6464E']

export default {
  name: 'TchImpactChange',
  components: { ImpactHeader, KpiCard, BarList, FilterPills },
  data () {
    return {
      loading: false,
      exporting: false,
      loadedAt: '',
      days: 90,
      type: '',
      newOnly: 0,
      tchId: null,
      switchStatus: '',
      teachers: [],
      dayOptions: DAY_OPTIONS,
      typeOptions: [
        { value: '', label: '全部' },
        { value: 'resign', label: '离职' },
        { value: 'close', label: '关闭时间' },
        { value: 'elim', label: '淘汰' }
      ],
      newOptions: [
        { value: 0, label: '全部学生' },
        { value: 1, label: '只看新生优先' }
      ],
      switchOptions: [
        { value: '', label: '全部' },
        { value: 'waiting', label: '待换老师' },
        { value: 'switched', label: '已换完' }
      ],
      d: {}
    }
  },
  computed: {
    params () {
      return {
        days: this.days,
        newOnly: this.newOnly,
        type: this.type,
        tchId: this.tchId,
        switchStatus: this.switchStatus
      }
    },
    list () {
      return this.d.list || []
    },
    byType () {
      return this.d.byType || []
    },
    chips () {
      return [
        { label: '时间窗口', value: '近 ' + this.days + ' 天' },
        { label: '异动类型', value: this.type ? typeLabel(this.type) : '全部' },
        { label: '学生范围', value: this.newOnly ? '只看新生优先' : '全部学生' },
        { label: '新生优先', value: '开课 ≤ 30 天' }
      ]
    },
    listSub () {
      return '近 ' + this.days + ' 天 · ' + (this.newOnly
        ? '仅看新生优先'
        : '新生优先 ' + fmtNum(this.d.newCount) + ' 人置顶，其余按风险分降序')
    },
    retainGap () {
      let d = this.d
      if (!isNum(d.keepRetain30Rate) || !isNum(d.changeRetain30Rate)) {
        return null
      }
      return Math.round((Number(d.keepRetain30Rate) - Number(d.changeRetain30Rate)) * 10) / 10
    },
    kpiCards () {
      let d = this.d
      let stu = num(d.studentCount)
      let lostShare = stu > 0 ? num(d.lostStudentCount) / stu * 100 : null
      let gap = this.retainGap
      return [
        {
          key: 'student',
          title: '受异动影响学生',
          value: fmtNum(d.studentCount),
          unit: '人',
          level: num(d.newCount) > 0 ? 'danger' : (stu > 0 ? 'warning' : 'healthy'),
          period: '敞口 ' + fmtNum(d.exposureHours) + ' 课时',
          compare: '新生优先 ' + fmtNum(d.newCount) + ' 人',
          compareTone: num(d.newCount) > 0 ? 'danger' : 'neutral',
          foot: '离职 / 淘汰 / 关闭时间波及、需要换老师的学生，去重'
        },
        {
          key: 'waiting',
          title: '待换老师',
          value: fmtNum(d.waitingCount),
          unit: '人',
          level: num(d.waitingOver7Count) > 0 ? 'danger' : (num(d.waitingCount) > 0 ? 'warning' : 'healthy'),
          period: '异动已生效、未在新老师处上课',
          compare: '超 7 天 ' + fmtNum(d.waitingOver7Count) + ' 人',
          compareTone: num(d.waitingOver7Count) > 0 ? 'danger' : 'neutral',
          foot: '最需要人工介入的名单'
        },
        {
          key: 'switch7',
          title: '7 天换完率',
          value: fmtRateNum(d.switched7dRate),
          unit: '%',
          level: levelHigh(d.switched7dRate, 80, 60),
          period: '7 天换完 ' + fmtNum(d.switched7dCount) + ' / 受影响 ' + fmtNum(d.studentCount),
          compare: '',
          foot: '生效后 7 天内已选新老师课 ÷ 受影响学生'
        },
        {
          key: 'switched',
          title: '已换老师',
          value: fmtNum(d.switchedCount),
          unit: '人',
          level: 'healthy',
          period: '已换完 ' + fmtNum(d.switchedCount) + ' / 受影响 ' + fmtNum(d.studentCount),
          compare: '',
          foot: '生效后首节已选课的老师 ≠ 原老师'
        },
        {
          key: 'keep',
          title: '保时率',
          value: fmtRateNum(d.keepSlotRate),
          unit: '%',
          level: levelHigh(d.keepSlotRate, 80, 60),
          period: '保时 ' + fmtNum(d.keepSlotCount) + ' 人',
          compare: '保时留存 ' + fmtRate(d.keepRetain30Rate),
          compareTone: 'healthy',
          foot: '新老师首课同星期、开始时间相差 ≤ 30 分钟 ÷ 已换老师'
        },
        {
          key: 'changeSlot',
          title: '换老师 + 改时间',
          value: fmtNum(d.changeSlotCount),
          unit: '人',
          level: levelLow(d.changeSlotRate, 15, 30),
          period: '占已换完 ' + fmtRate(d.changeSlotRate),
          compare: '改时留存 ' + fmtRate(d.changeRetain30Rate),
          compareTone: 'danger',
          foot: '没能保住原时段、被迫改约，是流失主力'
        },
        {
          key: 'gap',
          title: '保时 vs 改时留存差',
          value: gap == null ? '—' : String(gap),
          unit: 'pp',
          level: levelLow(gap, 5, 10),
          period: '30 天留存：保时 − 改时',
          compare: '',
          foot: '仅统计生效满 30 天的已换老师学生'
        },
        {
          key: 'lost',
          title: '累计损失课次',
          value: fmtNum(d.lostTotal),
          unit: '课次',
          level: num(d.lostTotal) <= 0 ? 'healthy' : levelLow(lostShare, 0, 30),
          period: '有损失学生 ' + fmtNum(d.lostStudentCount) + ' 人',
          compare: lostShare == null ? '' : '占 ' + fmtRate(lostShare),
          compareTone: 'danger',
          foot: '生效后 14 天应上（原周课量 × 2）− 实上'
        }
      ]
    },
    chainRows () {
      let d = this.d
      let stu = num(d.studentCount)
      let base = stu || 1
      let pct = function (v) {
        return stu > 0 ? fmtRate(num(v) / stu * 100) : '—'
      }
      return [
        { key: 'stu', label: '受影响学生', value: d.studentCount, rate: stu > 0 ? '100%' : '—', color: '#8FA3C7' },
        { key: 'switched', label: '已换完老师', value: d.switchedCount, rate: pct(d.switchedCount), color: '#5C78A8' },
        { key: 'switched7d', label: '7 天内换完', value: d.switched7dCount, rate: pct(d.switched7dCount), color: '#2A3C62' },
        { key: 'keep', label: '保住原时段', value: d.keepSlotCount, rate: fmtRate(d.keepSlotRate) + '（按已换）', color: '#0B9E8D' }
      ].map(function (row) {
        return Object.assign(row, { width: Math.max(num(row.value) / base * 100, num(row.value) > 0 ? 8 : 0) })
      })
    },
    depthRows () {
      return (this.d.depth || []).map(function (r, i) {
        return { key: r.label, label: r.label, value: r.count, color: DEPTH_COLORS[i] || DEPTH_COLORS[4] }
      })
    },
    depthInsight () {
      let depth = this.d.depth || []
      let stu = num(this.d.studentCount)
      if (!depth.length || stu <= 0) {
        return ''
      }
      let zero = num(depth[0].count)
      let hurt = stu - zero
      return '近 ' + this.days + ' 天 ' + fmtNum(stu) + ' 名受影响学生中，' + fmtNum(zero) + ' 人（' +
        fmtRate(zero / stu * 100) + '）零损失顺利过渡；' + fmtNum(hurt) + ' 人（' + fmtRate(hurt / stu * 100) +
        '）有真实课次损失，合计损失 ' + fmtNum(this.d.lostTotal) + ' 课次。'
    },
    keepRows () {
      return this.byType.map(function (r) {
        return {
          key: r.type,
          label: typeLabel(r.type),
          value: r.keepSlotRate,
          percent: num(r.keepSlotRate),
          valueText: fmtRate(r.keepSlotRate),
          extra: '已换 ' + fmtNum(r.switched),
          color: typeColor(r.type)
        }
      })
    },
    regapRows () {
      return (this.d.regap || []).map(function (r, i) {
        return { key: r.label, label: r.label, value: r.count, color: REGAP_COLORS[i] || REGAP_COLORS[4] }
      })
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    num: num,
    isNum: isNum,
    fmtNum: fmtNum,
    fmtRate: fmtRate,
    typeLabel: typeLabel,
    typeBadge: typeBadge,
    levelBadge (level) {
      return LEVEL_BADGE[level] || 'is-gray'
    },
    onTchChange () {
      if (this._suppressTchReload) {
        return
      }
      this.load()
    },
    load () {
      let self = this
      self.loading = true
      postImpact(self, '/change', self.params).then(function (data) {
        if (!data) {
          return
        }
        self.d = data
        self.teachers = data.teachers || []
        self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
        if (self.tchId != null && self.tchId !== '') {
          let found = self.teachers.some(function (t) {
            return t.id === self.tchId || String(t.id) === String(self.tchId)
          })
          if (!found) {
            self._suppressTchReload = true
            self.tchId = null
            self._suppressTchReload = false
            return postImpact(self, '/change', self.params).then(function (data2) {
              if (data2) {
                self.d = data2
                self.teachers = data2.teachers || []
                self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
              }
            })
          }
        }
      }).finally(function () {
        self.loading = false
      })
    },
    doExport () {
      if (this.exporting) {
        return
      }
      let self = this
      self.exporting = true
      exportImpact(self, Object.assign({ view: 'change' }, self.params), '外教异动受影响学生.xlsx').finally(function () {
        self.exporting = false
      })
    }
  }
}
</script>
<style scoped lang="less">
@import './parts/page.less';

.funnel-rate {
  flex-basis: 110px;
}

.list-filters {
  margin: 0 16px 8px;
}

.teacher-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.teacher-filter .filter-label {
  font-size: 12px;
  color: #8a94a6;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// TchImpactChange.vue?6cf6f3b4